import React from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';

import {
  INDETERMINATE,
  IndeterminateValue,
  isIndeterminate,
  PropertySelectField,
  PropertyTextInput,
} from '@paragon/client-tools';
import { ComponentSide, HeelMethod, HeelSettings } from '@paragon/contract-library';
import { Direction2D, distanceGreaterThan, distanceLessThan, LineSegment2D } from '@paragon/geometry-library';
import { DEFAULT_HEEL_SETTINGS, defaultTopChordSlopeInDegreesForHeelHeightCalculations } from '@paragon/layout';
import { DistanceField } from '@paragon/material';
import { assertExhaustiveSwitch, humanCase, secantOfSlope, slopeFromPoints } from '@paragon/utilities';

import { ProfileClass } from '../../../../library/profiles/ProfileClass';
import {
  updateInsertingProfile,
  updateInsertingProfileHeelHeight,
  updateInsertingProfileTrussSettings,
} from '../../../../store/actions/profileActions';
import {
  getBottomChordWidthDuringProfileInsertion,
  getInsertingProfileClass,
  getInsertingProfileTrussSettings,
  getTopChordWidthDuringProfileInsertion,
} from '../../../../store/selectors/profileSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';

/**
 * There are three different statuses of the seat cut on a certain side of the truss:
 *  - `SeatCutExists`: The profile already contains a seat cut on this side of the truss.
 *    - In this case the seat cut input will be readonly since we don't necessarily know how to edit the seat cut
 *      length if the profile is closed. (We could potentially make it editable if the profile is not closed yet.)
 *  - `NoSeatCutPossible`: The profile contains no seat cut on this side of the truss, and we've already moved past
 *    the stage where the user can add one.
 *    - In this case the seat cut input will be disabled and read "N/A."
 *  - `SeatCutPossible`: The profile contains no seat cut on this side of the truss, and the user can still add one
 *    (because the bottom chords that would prevent the seat cut haven't been added yet).
 *    - In this case the seat cut is enabled and will control the seat cut that is added if the user puts in a sloped
 *      bottom chord on the left or closes the profile on the right.
 */
function getSeatCutInfo(
  profile: ProfileClass,
  side: ComponentSide,
):
  { status: 'SeatCutExists'; seatCutLength: number } | { status: 'NoSeatCutPossible' } | { status: 'SeatCutPossible' } {
  switch (side) {
    case 'Left':
      if (profile.leftSeatCut) {
        return {
          status: 'SeatCutExists',
          seatCutLength: profile.leftSeatCut.length,
        };
      }

      return profile.bottomChords.length > 0 ? { status: 'NoSeatCutPossible' } : { status: 'SeatCutPossible' };
    case 'Right':
      if (profile.rightSeatCut) {
        return {
          status: 'SeatCutExists',
          seatCutLength: profile.rightSeatCut.length,
        };
      }

      return profile.isClosed ? { status: 'NoSeatCutPossible' } : { status: 'SeatCutPossible' };
    default:
      assertExhaustiveSwitch(side);
  }
}

function SeatCutInput({ profile, side }: { profile: ProfileClass; side: ComponentSide }) {
  const dispatch = useAppDispatch();

  const seatCutInfo = getSeatCutInfo(profile, side);
  const onSeatCutChange = async (value: number) => {
    const propertyToUpdate = side === 'Left' ? 'leftSeatCut' : 'rightSeatCut';
    await dispatch(
      updateInsertingProfileTrussSettings({
        ...profile.settingsData,
        [propertyToUpdate]: value,
      }),
    );
  };

  switch (seatCutInfo.status) {
    case 'SeatCutExists':
      return <DistanceField label="Seat Cut" value={seatCutInfo.seatCutLength} disabled />;
    case 'NoSeatCutPossible':
      return <PropertyTextInput property="Seat Cut" value="N/A" disabled />;
    case 'SeatCutPossible':
      return (
        <DistanceField
          label="Seat Cut"
          value={side === 'Left' ? profile.settingsData.leftSeatCut : profile.settingsData.rightSeatCut}
          onSubmit={onSeatCutChange}
          min={0}
        />
      );
    default:
      assertExhaustiveSwitch(seatCutInfo);
  }
}

function getMemberVerticalWidth(memberWidth: number, memberDirection: Direction2D) {
  return memberWidth / memberDirection.x;
}

function updateHeelSettingsHeelMethod(
  heelSettings: HeelSettings,
  newHeelMethod: HeelMethod,
  topChordWidth: number,
  topChordSlopeInDegrees: number,
): HeelSettings {
  if (heelSettings.heelMethod === newHeelMethod) return heelSettings;

  switch (newHeelMethod) {
    case 'HeelHeight':
      const topChordDirection = Direction2D.fromAngle(topChordSlopeInDegrees);
      const heelHeight = getMemberVerticalWidth(topChordWidth, topChordDirection) + 0.25;
      return { ...heelSettings, heelMethod: 'HeelHeight', heelHeight };
    case 'ButtCut':
      return { ...heelSettings, heelMethod: 'ButtCut', buttCut: 0.25 };
    default:
      assertExhaustiveSwitch(newHeelMethod);
  }
}

function HeelHeightOrButtCutInput({
  heelSettings,
  bottomChordWidth,
  submit,
  disabled,
}: {
  readonly heelSettings: HeelSettings;
  readonly bottomChordWidth?: number;
  readonly submit: (heelSettings: HeelSettings) => Promise<void>;
  readonly disabled?: boolean;
}) {
  switch (heelSettings.heelMethod) {
    case 'HeelHeight':
      return (
        <DistanceField
          label="Heel Height"
          value={heelSettings.heelHeight}
          onSubmit={async (heelHeight) => submit({ ...heelSettings, heelHeight })}
          min={0}
          disabled={disabled}
        />
      );
    case 'ButtCut':
      return (
        <DistanceField
          label="Butt Cut"
          value={heelSettings.buttCut}
          onSubmit={async (buttCut) => submit({ ...heelSettings, buttCut })}
          min={0}
          max={bottomChordWidth}
          disabled={disabled}
        />
      );
    default:
      assertExhaustiveSwitch(heelSettings);
  }
}

export const PrimaryHeelSettingsMethodInputs = ({
  heelSettings,
  topChordWidth,
  bottomChordWidth,
  topChordSlopeInDegrees,
  submit,
  disabled,
}: {
  readonly heelSettings: HeelSettings | IndeterminateValue;
  readonly topChordWidth: number;
  readonly bottomChordWidth?: number;
  readonly topChordSlopeInDegrees: number;
  readonly submit: (heelSettings: HeelSettings) => Promise<void>;
  readonly disabled?: boolean;
}) => {
  const heelSettingsOrDefault = isIndeterminate(heelSettings) ? DEFAULT_HEEL_SETTINGS : heelSettings;
  return (
    <>
      <PropertySelectField<HeelMethod>
        property="Heel Method"
        value={isIndeterminate(heelSettings) ? INDETERMINATE : heelSettings.heelMethod}
        options={['HeelHeight', 'ButtCut']}
        submit={async (newHeelMethod) => {
          await submit(
            updateHeelSettingsHeelMethod(heelSettingsOrDefault, newHeelMethod, topChordWidth, topChordSlopeInDegrees),
          );
        }}
        getName={humanCase}
        disabled={disabled}
      />

      {isIndeterminate(heelSettings) ? (
        <DistanceField canBeIndeterminate disabled label="Heel Height/Butt Cut" value={INDETERMINATE} />
      ) : (
        <HeelHeightOrButtCutInput
          heelSettings={heelSettings}
          bottomChordWidth={bottomChordWidth}
          submit={async (heelSettings) => await submit(heelSettings)}
          disabled={disabled}
        />
      )}
    </>
  );
};

function HeelSettingsBasedOnHeelSettings({
  side,
  condensed,
}: {
  side: ComponentSide;
  condensed?: boolean | undefined;
}) {
  const dispatch = useAppDispatch();
  const profileTrussSettings = useAppSelector(getInsertingProfileTrussSettings);
  const profileClassResult = useAppSelector(getInsertingProfileClass);
  const topChordWidth = useAppSelector(getTopChordWidthDuringProfileInsertion);
  const bottomChordWidth = useAppSelector(getBottomChordWidthDuringProfileInsertion);

  const heelSettings =
    side === 'Left' ? profileTrussSettings.profile.leftHeelSettings : profileTrussSettings.profile.rightHeelSettings;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1, justifyContent: 'space-evenly' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <PrimaryHeelSettingsMethodInputs
          heelSettings={heelSettings}
          topChordWidth={topChordWidth}
          bottomChordWidth={bottomChordWidth}
          topChordSlopeInDegrees={defaultTopChordSlopeInDegreesForHeelHeightCalculations}
          submit={async (heelSettings) => {
            const heelSettingsProperty = side === 'Left' ? 'leftHeelSettings' : 'rightHeelSettings';
            await dispatch(
              updateInsertingProfile({ ...profileTrussSettings.profile, [heelSettingsProperty]: heelSettings }),
            );
          }}
        />
      </Box>

      {profileClassResult.type === 'Ok' && condensed !== true && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <SeatCutInput profile={profileClassResult.ok} side={side} />
            <InteriorButtCutInput
              profile={profileClassResult.ok}
              side={side}
              topChordVerticalWidth={getTopChordVerticalWidth(profileClassResult.ok, side, topChordWidth)}
              bottomChordWidth={bottomChordWidth}
            />
          </Box>
          <Box sx={{ display: 'flex' }}>
            <FormControlLabel
              control={
                <Checkbox
                  color="default"
                  checked={heelSettings.chordTypeRunThrough === 'Bottom'}
                  onChange={async (_, checked) => {
                    const updatedHeelSettings = { ...heelSettings, chordTypeRunThrough: checked ? 'Bottom' : 'Top' };
                    const heelSettingsProperty = side === 'Left' ? 'leftHeelSettings' : 'rightHeelSettings';
                    await dispatch(
                      updateInsertingProfile({
                        ...profileTrussSettings.profile,
                        [heelSettingsProperty]: updatedHeelSettings,
                      }),
                    );
                  }}
                />
              }
              label="Run bottom chord through"
              sx={{ margin: 'auto' }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

function getTopChordVerticalWidthCoefficient(profile: ProfileClass, side: ComponentSide) {
  const topChordProfileLine = side === 'Left' ? profile.topChords.at(0) : profile.topChords.at(-1);
  if (!topChordProfileLine) return 1;

  const { basePoint, endPoint } = topChordProfileLine;
  return secantOfSlope(slopeFromPoints(basePoint.x, basePoint.y, endPoint.x, endPoint.y));
}

function getTopChordVerticalWidth(profile: ProfileClass, side: ComponentSide, topChordWidth: number) {
  return topChordWidth * getTopChordVerticalWidthCoefficient(profile, side);
}

function getButtCut(topChordVerticalWidth: number, bottomChordWidth: number, heelGeometry: LineSegment2D | undefined) {
  if (!heelGeometry) return null;
  const heelHeight = heelGeometry.length;

  if (distanceLessThan(heelHeight, topChordVerticalWidth)) return null;
  if (distanceGreaterThan(heelHeight, topChordVerticalWidth + bottomChordWidth)) return null;
  return heelHeight - topChordVerticalWidth;
}

function InteriorButtCutInput({
  profile,
  side,
  topChordVerticalWidth,
  bottomChordWidth,
}: {
  profile: ProfileClass;
  side: ComponentSide;
  topChordVerticalWidth: number;
  bottomChordWidth: number;
}) {
  const dispatch = useAppDispatch();

  const heelGeometry = side === 'Left' ? profile.leftHeel?.segment : profile.rightHeel?.segment;

  if (heelGeometry && distanceGreaterThan(heelGeometry.length, topChordVerticalWidth)) {
    return <PropertyTextInput property="Interior Butt Cut" value="N/A" disabled />;
  }

  const heelSettings = side === 'Left' ? profile.profileData.leftHeelSettings : profile.profileData.rightHeelSettings;
  return (
    <DistanceField
      label="Interior Butt Cut"
      value={heelSettings.interiorButtCut}
      onSubmit={async (newInteriorButtCut) => {
        const updatedHeelSettings = { ...heelSettings, interiorButtCut: newInteriorButtCut };
        const heelSettingsProperty = side === 'Left' ? 'leftHeelSettings' : 'rightHeelSettings';
        await dispatch(updateInsertingProfile({ ...profile.profileData, [heelSettingsProperty]: updatedHeelSettings }));
      }}
      min={0}
      max={bottomChordWidth}
    />
  );
}

function getMinHeelHeight(profile: ProfileClass) {
  const leftHeelHeight = profile.leftHeel?.length ?? 0;
  const rightHeelHeight = profile.rightHeel?.length ?? 0;

  const leftHeelHeightSource = profile.getLeftHeelHeightSource();
  const rightHeelHeightSource = profile.getRightHeelHeightSource();

  if (rightHeelHeightSource !== 'ProfileGeometry') {
    if (leftHeelHeightSource !== 'ProfileGeometry')
      throw new Error('Expected left heel to be based on profile geometry.');

    return leftHeelHeight;
  }

  return Math.min(leftHeelHeight, rightHeelHeight);
}

function HeelSettingsBasedOnProfileGeometry({
  profile,
  side,
  condensed,
}: {
  profile: ProfileClass;
  side: ComponentSide;
  condensed?: boolean | undefined;
}) {
  const dispatch = useAppDispatch();
  const topChordWidth = useAppSelector(getTopChordWidthDuringProfileInsertion);
  const bottomChordWidth = useAppSelector(getBottomChordWidthDuringProfileInsertion);

  const heelGeometry = side === 'Left' ? profile.leftHeel?.segment : profile.rightHeel?.segment;
  const topChordVerticalWidth = getTopChordVerticalWidth(profile, side, topChordWidth);
  const buttCut = getButtCut(topChordVerticalWidth, bottomChordWidth, heelGeometry);
  const heelSettings = side === 'Left' ? profile.profileData.leftHeelSettings : profile.profileData.rightHeelSettings;

  const heelHeight = heelGeometry?.length ?? 0;
  const minAllowedHeelHeight = heelHeight - getMinHeelHeight(profile);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1, justifyContent: 'space-evenly' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <DistanceField
          label="Heel Height"
          value={heelHeight}
          onSubmit={async (newHeelHeight) => {
            await dispatch(updateInsertingProfileHeelHeight(profile, newHeelHeight, side));
          }}
          min={minAllowedHeelHeight}
        />
        {buttCut == null ? (
          <PropertyTextInput property="Butt Cut" value="N/A" disabled />
        ) : (
          <DistanceField
            label="Butt Cut"
            value={buttCut}
            onSubmit={async (newButtCut) => {
              const newHeelHeight = newButtCut + topChordVerticalWidth;
              await dispatch(updateInsertingProfileHeelHeight(profile, newHeelHeight, side));
            }}
            min={0}
            max={bottomChordWidth}
          />
        )}
      </Box>

      {condensed !== true && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <SeatCutInput profile={profile} side={side} />
            <InteriorButtCutInput
              profile={profile}
              side={side}
              topChordVerticalWidth={topChordVerticalWidth}
              bottomChordWidth={bottomChordWidth}
            />
          </Box>
          <Box sx={{ display: 'flex' }}>
            <FormControlLabel
              control={
                <Checkbox
                  color="default"
                  checked={heelSettings.chordTypeRunThrough === 'Bottom'}
                  onChange={async (_, checked) => {
                    const updatedHeelSettings = { ...heelSettings, chordTypeRunThrough: checked ? 'Bottom' : 'Top' };
                    const heelSettingsProperty = side === 'Left' ? 'leftHeelSettings' : 'rightHeelSettings';
                    await dispatch(
                      updateInsertingProfile({ ...profile.profileData, [heelSettingsProperty]: updatedHeelSettings }),
                    );
                  }}
                />
              }
              label="Run bottom chord through"
              sx={{ margin: 'auto' }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default function HeelSettingsSubPanel({ side, condensed }: { side: ComponentSide; condensed?: boolean }) {
  const profileResult = useAppSelector(getInsertingProfileClass);

  // Use heel settings if there's an issue with the profile class
  if (profileResult.type === 'Err') {
    return <HeelSettingsBasedOnHeelSettings side={side} condensed={condensed} />;
  }

  const profile = profileResult.ok;
  const heelHeightSource = side === 'Left' ? profile.getLeftHeelHeightSource() : profile.getRightHeelHeightSource();

  switch (heelHeightSource) {
    case 'HeelSettings':
      return <HeelSettingsBasedOnHeelSettings side={side} condensed={condensed} />;
    case 'ProfileGeometry':
      return <HeelSettingsBasedOnProfileGeometry profile={profile} side={side} condensed={condensed} />;
    default:
      assertExhaustiveSwitch(heelHeightSource);
  }
}
